const SLAB_ONLY_PART_IDS = [
  'slabPlaceHolder',
  'seamPiece',
  'backsplashPiece',
  'waterfallPiece',
];

const RESOURCE_TYPE_LABELS = {
  backsplash: 'messageBox.fatalErrorReviewBacksplashes',
  bumpout: 'messageBox.fatalErrorReviewBumpouts',
  crown_molding: 'messageBox.fatalErrorReviewAdditionalDetails',
  edge: 'messageBox.fatalErrorReviewEdges',
  end_cap: 'messageBox.fatalErrorReviewAdditionalDetails',
  mitered: 'messageBox.fatalErrorReviewAdditionalDetails',
  underlayment: 'messageBox.fatalErrorReviewUnderlayment',
  waterfall: 'messageBox.fatalErrorReviewAdditionalDetails',
  wall: 'messageBox.fatalErrorReviewAdditionalDetails',
};

const PART_ID_LABELS = {
  backsplash: 'sqft.contextmenu.labels.backsplash',
  backsplashes: 'sqft.contextmenu.labels.backsplash',
  center_island_countertop: 'sqft.contextmenu.labels.centerIsland',
  countertop: 'sqft.contextmenu.labels.countertop',
  vanity_backsplash: 'sqft.contextmenu.labels.backsplash',
  vanity_countertop: 'sqft.contextmenu.labels.countertop',
};

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function parseCalculatorData(value) {
  if (!value) {
    return null;
  }

  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

function getCartItemName(cartItem) {
  const feature = cartItem && cartItem.cartFeature;

  try {
    if (feature && typeof feature.getFeatureName === 'function') {
      return feature.getFeatureName();
    }
  } catch (error) {
    // Fatal recovery must still render when a feature object is only partially available.
  }

  if (feature && feature.dbFeature) {
    return feature.dbFeature.sharedFeatureName
      || feature.dbFeature.groupName
      || feature.partName
      || cartItem.partId;
  }

  return (feature && feature.partName) || cartItem.partId || '';
}

function getCartItemUnit(cartItem) {
  if (cartItem.unit) {
    return cartItem.unit;
  }

  const dbFeature = cartItem.cartFeature && cartItem.cartFeature.dbFeature;

  return dbFeature && dbFeature.unit || '';
}

function getRoomName(quote, quoteCartItems, roomIndex) {
  const roomCartItem = quoteCartItems.find(cartItem => cartItem.partId === 'room');

  return quote.qtCatName
    || (roomCartItem && roomCartItem.qtCatName)
    || (roomCartItem && getCartItemName(roomCartItem))
    || quote.room
    || 'Room ' + (roomIndex + 1);
}

function getCartItemQuantity(cartItem) {
  if (cartItem.partId === 'room') {
    return null;
  }

  return cartItem.displayFakeQuantity !== undefined && cartItem.displayFakeQuantity !== null
    ? cartItem.displayFakeQuantity
    : cartItem.quantity;
}

function getShapeFeatureName(shape) {
  const firstFeature = toArray(shape.features)[0];

  return firstFeature && firstFeature.featureName || shape.featureName || '';
}

function addCalculatorEntry(entries, entryToAdd) {
  const existingEntry = entries.find(entry => (
    entry.labelId === entryToAdd.labelId
    && entry.fallbackLabel === entryToAdd.fallbackLabel
    && entry.featureName === entryToAdd.featureName
  ));

  if (existingEntry) {
    existingEntry.count += entryToAdd.count;
    return;
  }

  entries.push(entryToAdd);
}

function humanizePartId(partId) {
  if (!partId) {
    return '';
  }

  return partId
    .replace(/_/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
}

function getSlabEntries(calculatorData, countertops) {
  const slabAreaItems = toArray(calculatorData.slabImagesTotalArea);

  return slabAreaItems.map(slab => {
    const matchingShape = countertops.find(shape => (
      shape.partId === 'slabPlaceHolder'
      && shape.featureId === slab.featureId
    ));

    return {
      count: Number(slab.totalSlabs) || 1,
      fallbackLabel: '',
      featureName: matchingShape ? getShapeFeatureName(matchingShape) : '',
      labelId: 'messageBox.fatalErrorReviewSlabs',
    };
  });
}

function getManualCalculatorEntries(calculatorData) {
  const entries = [];
  const manualGroups = [{
    items: calculatorData.countertops,
    labelId: 'sqft.contextmenu.labels.countertop',
  }, {
    items: calculatorData.backsplashes,
    labelId: 'sqft.contextmenu.labels.backsplash',
  }, {
    items: calculatorData.edges,
    labelId: 'messageBox.fatalErrorReviewEdges',
  }];

  manualGroups.forEach(group => {
    const count = toArray(group.items).length;

    if (count) {
      entries.push({
        count,
        fallbackLabel: '',
        featureName: '',
        labelId: group.labelId,
      });
    }
  });

  return entries;
}

export function summarizeCalculatorData(value) {
  const calculatorData = parseCalculatorData(value);

  if (!calculatorData) {
    return null;
  }

  const entries = calculatorData.manualCalculate
    ? getManualCalculatorEntries(calculatorData)
    : [];
  const countertops = toArray(calculatorData.countertops);

  if (!calculatorData.manualCalculate) {
    countertops.forEach(shape => {
      if (!shape || !Array.isArray(shape.lines) || SLAB_ONLY_PART_IDS.includes(shape.partId)) {
        return;
      }

      const labelId = PART_ID_LABELS[shape.partId] || '';

      addCalculatorEntry(entries, {
        count: 1,
        fallbackLabel: labelId ? '' : humanizePartId(shape.partId || shape.type),
        featureName: getShapeFeatureName(shape),
        labelId,
      });
    });

    countertops.forEach(resource => {
      if (!resource || !resource.type || !RESOURCE_TYPE_LABELS[resource.type]) {
        return;
      }

      addCalculatorEntry(entries, {
        count: 1,
        fallbackLabel: humanizePartId(resource.type),
        featureName: resource.name || '',
        labelId: RESOURCE_TYPE_LABELS[resource.type],
      });
    });
  }

  getSlabEntries(calculatorData, countertops).forEach(entry => addCalculatorEntry(entries, entry));

  const countEntries = [{
    count: toArray(calculatorData.sinks).length,
    labelId: 'messageBox.fatalErrorReviewSinks',
  }, {
    count: toArray(calculatorData.bumpouts).length,
    labelId: 'messageBox.fatalErrorReviewBumpouts',
  }, {
    count: toArray(calculatorData.radii).length,
    labelId: 'messageBox.fatalErrorReviewRadii',
  }, {
    count: toArray(calculatorData.corners).length,
    labelId: 'messageBox.fatalErrorReviewCorners',
  }, {
    count: toArray(calculatorData.underlayment).length,
    labelId: 'messageBox.fatalErrorReviewUnderlayment',
  }];

  countEntries.forEach(entry => {
    if (entry.count) {
      addCalculatorEntry(entries, {
        ...entry,
        fallbackLabel: '',
        featureName: '',
      });
    }
  });

  return {
    area: Number(calculatorData.squareFeet) || 0,
    areaUnit: calculatorData.measureUnit === 'Metric' ? 'm²' : 'sq ft',
    entries,
  };
}

export function buildFatalErrorRecoverySummary(state, calculatorDataOverrides = {}) {
  const quotes = state && state.quotesState && Array.isArray(state.quotesState.quotes)
    ? state.quotesState.quotes
    : [];
  const allCartItems = state && state.cartState && Array.isArray(state.cartState.cartItems)
    ? state.cartState.cartItems
    : [];

  const rooms = quotes.map((quote, roomIndex) => {
    const quoteCartItems = allCartItems.filter(cartItem => (
      cartItem
      && cartItem.currentQuote === roomIndex
      && cartItem.cartFeature
      && cartItem.cartFeature.dbFeature
    ));
    const calculatorData = Object.prototype.hasOwnProperty.call(calculatorDataOverrides, roomIndex)
      ? calculatorDataOverrides[roomIndex]
      : quote.sqFtCalcData;

    return {
      calculator: summarizeCalculatorData(calculatorData),
      cartItems: quoteCartItems.map(cartItem => ({
        id: cartItem.id,
        name: getCartItemName(cartItem),
        quantity: getCartItemQuantity(cartItem),
        unit: cartItem.partId === 'room' ? '' : getCartItemUnit(cartItem),
      })),
      index: roomIndex,
      name: getRoomName(quote, quoteCartItems, roomIndex),
    };
  });

  return {
    cartItemCount: rooms.reduce((total, room) => total + room.cartItems.length, 0),
    rooms,
  };
}
